import { TimePicker } from '@components/TimePicker';
import {
  Card,
  DatePicker,
  Stack,
  TableCell,
  TableRow,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { Button, SelectInput, PhoneInput } from '@components';
import {
  orderStore,
  PickUpDropOffItem,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { CheckboxMarkedOutlineIcon } from '@atgfr8/icons';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { sanitizeStateInput } from '@utils/formUtils';
import { convertDateTimeV2 } from '@utils/utils';
import { Component } from 'solid-js';

import EDICompareChangeItems from './EDICompareChangeItems';

type StopEditSectionProps = {
  colspan: number;
  stop: LastUsedTopStopModel;
  noUpdate: boolean;
  items: PickUpDropOffItem[];
  index: number;
};

type UpdateButtonProps = {
  onClick: () => void;
  classes: string;
  noUpdate: boolean;
};

const StopEditSection: Component<StopEditSectionProps> = (props) => {
  const handleStopFieldChanges = (
    field: keyof LastUsedTopStopModel,
    value: string,
  ) => {
    const updateStops = orderStore.order.loads[0].stops?.map((stop) => {
      if (stop.id === props.stop.id) {
        return { ...stop, [field]: value };
      }
      return stop;
    });
    updateLoadPropertyAtIndex(
      {
        stops: updateStops,
      },
      0,
    );
  };

  const handleUpdateButtonClick = (
    values: Partial<LastUsedTopStopModel>,
    idx: number,
  ) => {
    const stops =
      orderStore.order.loads[0].stops?.filter(
        (itm) => itm.operationType !== 'Delete',
      ) ?? [];

    if (stops.length > 0) {
      const updatedStops = orderStore.order.loads[0].stops?.map((stop) => {
        if (stop.id === stops[idx].id) {
          return { ...stop, ...values };
        }
        return stop;
      });
      updateLoadPropertyAtIndex(
        {
          stops: updatedStops,
        },
        0,
      );
    }
  };

  const handleDateTimeChange = (date: string, time: string) => {
    const dateTime = convertDateTimeV2(date, time);
    handleStopFieldChanges('stopDateTime', dateTime);
  };

  const UpdateButton: Component<UpdateButtonProps> = (props) => {
    return !props.noUpdate ? (
      <div class={`${props.classes} text-right mt-auto mb-[2px]`}>
        <Button
          startIcon={<CheckboxMarkedOutlineIcon class="text-[#ffffff]" />}
          label="UPDATE"
          class="updateRateClass bg-[#388E3C]!"
          variant="contained"
          onClick={props.onClick}
          dataTestId={formatDataTestId('UPDATE', 'button')}
        />
      </div>
    ) : null;
  };

  return (
    <TableRow>
      <TableCell colSpan={props.colspan} class="p-0! mb-2!">
        <Card class="p-4 border! border border-transparent rounded-tl-none! rounded-tr-none! mx-px bg-white! mb-2">
          <div class="flex mb-[24px]">
            <div class="w-[50%]">
              <TextField
                label="Stop Name"
                required
                placeholder="Stop Name"
                value={props.stop.locationName as string}
                onChange={(itm: string) =>
                  handleStopFieldChanges('locationName', itm)
                }
                disabled={!props.noUpdate}
                dataTestId="stop-name-textInput"
              />
            </div>
            <UpdateButton
              onClick={() => {
                handleUpdateButtonClick(
                  {
                    locationName: props.stop.locationName,
                  },
                  props.index,
                );
              }}
              classes="w-[50%]"
              noUpdate={props.noUpdate}
            />
          </div>
          <div class="flex mb-[24px]">
            <Stack spacing={1.5}>
              <TextField
                label={
                  <span>
                    Address line 1<sup>*</sup>
                  </span>
                }
                placeholder="Enter Address"
                value={props.stop.address1 as string}
                onChange={(itm: string) =>
                  handleStopFieldChanges('address1', itm)
                }
                disabled={!props.noUpdate}
                dataTestId="address-line-1-textInput"
              />
              <TextField
                label="Address line 2"
                placeholder="Address line 2"
                value={props.stop.address2 as string}
                onChange={(itm: string) =>
                  handleStopFieldChanges('address2', itm)
                }
                disabled={!props.noUpdate}
                dataTestId="address-line-2-textInput"
              />
              <div class="flex mt-2">
                <div class="flex gap-2 w-[70%]">
                  <TextField
                    label="City"
                    required
                    placeholder="City"
                    value={props.stop.city as string}
                    onChange={(itm: string) =>
                      handleStopFieldChanges('city', itm)
                    }
                    class="w-[96px]! min-w-[96px]! max-w-[96px]!"
                    disabled={!props.noUpdate}
                    dataTestId="city-textInput"
                  />
                  <TextField
                    label="State"
                    required
                    placeholder="State"
                    value={props.stop.state as string}
                    onChange={(itm: string) => {
                      const sanitized = sanitizeStateInput(itm);
                      if (sanitized === (props.stop.state as string)) return;
                      handleStopFieldChanges('state', sanitized);
                    }}
                    maxLength={2}
                    class="mr-2!"
                    disabled={!props.noUpdate}
                    dataTestId="state-textInput"
                  />
                  <TextField
                    label="Zip"
                    required
                    placeholder="Zip"
                    value={props.stop.zip}
                    onChange={(itm: string) =>
                      handleStopFieldChanges('zip', itm)
                    }
                    class="mr-2!"
                    disabled={!props.noUpdate}
                    dataTestId="zip-textInput"
                  />
                  <SelectInput
                    label="USA/CAN/MEX"
                    menuItems={[
                      { label: 'USA', value: 'USA' },
                      { label: 'CAN', value: 'CAN' },
                      { label: 'MEX', value: 'MEX' },
                    ]}
                    value={props.stop.countryCode as string}
                    onChange={(val) =>
                      handleStopFieldChanges('countryCode', val as string)
                    }
                    disabled={!props.noUpdate}
                    dataTestId={formatDataTestId(
                      'EDI stop country',
                      'selectInput',
                    )}
                  />
                </div>
                <UpdateButton
                  onClick={() => {
                    handleUpdateButtonClick(
                      {
                        zip: props.stop.zip,
                        state: props.stop.state,
                        countryCode: props.stop.countryCode,
                        address1: props.stop.address1,
                        address2: props.stop.address2,
                        city: props.stop.city,
                      },
                      props.index,
                    );
                  }}
                  classes="w-[30%]"
                  noUpdate={props.noUpdate}
                />
              </div>
            </Stack>
          </div>
          <div class="flex mb-[24px]">
            <div class="flex w-[70%]">
              <div class="w-[50%] pr-4">
                <DatePicker
                  label=""
                  value={props.stop.stopDateTime as string}
                  handleChange={(date: string) =>
                    handleDateTimeChange(date, props.stop.stopDateTime ?? '')
                  }
                  disabled={!props.noUpdate}
                  dataTestId="stop-date-datePicker"
                />
              </div>
              <div class="w-[50%] pr-4">
                <TimePicker
                  label="Time"
                  dataTestId="stop-time-timePicker"
                  value={props.stop.stopDateTime as string}
                  onChange={(newTime) => {
                    handleDateTimeChange(
                      props.stop.stopDateTime ?? '',
                      newTime,
                    );
                  }}
                  disabled={!props.noUpdate}
                />
              </div>
            </div>
            <UpdateButton
              onClick={() => {
                handleUpdateButtonClick(
                  {
                    stopDateTime: props.stop.stopDateTime,
                  },
                  props.index,
                );
              }}
              classes="w-[30%]"
              noUpdate={props.noUpdate}
            />
          </div>
          <div class="mb-[24px] flex">
            <div class="w-[80%]">
              <TextField
                label="Appointment #"
                required
                placeholder=""
                value={props.stop.appointmentNum ?? ''}
                onChange={(itm: string) =>
                  handleStopFieldChanges('appointmentNum', itm)
                }
                disabled={!props.noUpdate}
                dataTestId="appointment-number-textInput"
              />
            </div>
            <UpdateButton
              onClick={() => {
                handleUpdateButtonClick(
                  {
                    appointmentNum: props.stop.appointmentNum,
                  },
                  props.index,
                );
              }}
              classes="w-[20%]"
              noUpdate={props.noUpdate}
            />
          </div>
          <div class="mb-[24px] flex">
            <div class="w-[80%]">
              <TextField
                label="PickUp #"
                required
                placeholder=""
                value={props.stop.referenceNum ?? ''}
                onChange={(itm: string) =>
                  handleStopFieldChanges('referenceNum', itm)
                }
                disabled={!props.noUpdate}
                dataTestId="pickup-number-textInput"
              />
            </div>
            <UpdateButton
              onClick={() => {
                handleUpdateButtonClick(
                  {
                    referenceNum: props.stop.referenceNum,
                  },
                  props.index,
                );
              }}
              classes="w-[20%]"
              noUpdate={props.noUpdate}
            />
          </div>
          <div class="mb-[24px] flex">
            <div class="w-[80%]">
              <Stack spacing={1.5}>
                <TextField
                  label={'Name'}
                  placeholder="Name"
                  value={props.stop.contact as string}
                  onChange={(itm: string) =>
                    handleStopFieldChanges('contact', itm)
                  }
                  disabled={!props.noUpdate}
                  dataTestId="name-textInput"
                />
                <PhoneInput
                  label="Phone"
                  placeholder="Phone Number"
                  value={props.stop.phone ?? ''}
                  onChange={(value) => handleStopFieldChanges('phone', value)}
                  disabled={!props.noUpdate}
                  dataTestId="phone-phoneInput"
                />
                <TextField
                  label="Email"
                  placeholder="Email"
                  value={props.stop.email ?? ''}
                  onChange={(itm: string) =>
                    handleStopFieldChanges('email', itm)
                  }
                  disabled={!props.noUpdate}
                  dataTestId="email-textInput"
                />
              </Stack>
            </div>
            <UpdateButton
              onClick={() => {
                handleUpdateButtonClick(
                  {
                    email: props.stop.email,
                    phone: props.stop.phone,
                    contact: props.stop.contact,
                  },
                  props.index,
                );
              }}
              classes="w-[20%]"
              noUpdate={props.noUpdate}
            />
          </div>
          <EDICompareChangeItems
            noUpdate={props.noUpdate}
            stop={props.stop}
            items={props.items}
            index={props.index}
          />
        </Card>
      </TableCell>
    </TableRow>
  );
};

export default StopEditSection;
